import React from 'react'
import { useState, useEffect } from 'react'
import CircularProgress from '@mui/material/CircularProgress'
import { ThemeOptions } from '@mui/system'
import { ResponsiveContainer } from '../../../layout'
export interface CardProps {
  onSuccess: (data: CardAdditionSuccess) => void
  onError: (error: CardAdditionError) => void
  onClickBack: () => void
  clientSecret: string
  parentOrigin: string
  payerId: string
  publicKey: string
  environment: string
  theme: ThemeOptions
  isStart?: boolean
  disableSubmitOnSuccess?: boolean
  submitButtonText?: boolean
  sx?: {
    container?: object
    iframe?: object
  }
}

export interface CardAdditionSuccess {
  id: string
  payer: {
    id: string
  }
  data: object
  status: string
  statusReasons: Array<object>
  type: string
}

export interface CardAdditionError {
  code: string
  errors: Array<object>
  message: string
  traceId: string
}

// TODO: update data type declaration
export interface EventData {
  type: 'iframeLoaded' | 'success' | 'error' | 'onClickBack'
  data: any // TODO: define data object type
}

/**
 * @param {CardProps} props
 * @param {function} onSuccess - Callback fired when iFrame send onSuccess Event
 * @param {function} onError - Callback fired when iFrame sends onError Event
 * @param {function} onClickBack - Callback fired when back button is clicked
 * @param {string} clientSecret - Client Secret
 * @param {string} payerId - Payer Id
 * @param {string} parentOrigin - Parent Origin
 * @param {string} publicKey - Public Key
 * @param {string} environment - API host Environment
 * @param {object} theme - mui theme config
 * @param {boolean} isStart - whether the card form is the start of flow (determines back button rendering)
 * @param {boolean} disableSubmitOnSuccess - whether the submit button should be disabled after successful card submission
 * @param {string} submitButtonText - text for submit button
 * @param {object} sx - styles for form container and iframe
 */

const Card = (props: CardProps) => {
  let iFrame: HTMLIFrameElement
  const [isLoading, setIsLoading] = useState(true)
  const [isIframeLoaded, setIsIframeLoaded] = useState(false)
  const origin: string = window.location.origin
  let captureAppOrigin: string = origin.match('local')
    ? 'http://localhost:3151'
    : 'https://ui.payment-method-components.cde.plastiq.com'
  if (origin.match('qa.plastiq.com')) {
    captureAppOrigin = captureAppOrigin.replace('cde', 'qa-cde')
  }
  if (
    origin.match('preprod.plastiq.com') ||
    origin.match('sandbox.plastiq.com')
  ) {
    captureAppOrigin = captureAppOrigin.replace('cde', 'preprod-cde')
  }
  if (origin.match('dev.plastiq.com')) {
    captureAppOrigin = captureAppOrigin.replace('cde', 'dev-cde')
  }

  const listenerHandler = (event: MessageEvent) => {
    if (event.origin === captureAppOrigin) {
      const eventData: EventData = event.data
      switch (eventData?.type) {
        case 'iframeLoaded': {
          setIsIframeLoaded(true)
          const message = {
            type: 'loadConfig',
            data: {
              clientSecret: props.clientSecret,
              payerId: props.payerId,
              publicKey: props.publicKey,
              parentOrigin: props.parentOrigin,
              theme: props.theme,
              environment: props.environment,
              isStart: props.isStart,
              disableSubmitOnSuccess: props.disableSubmitOnSuccess,
              submitButtonText: props.submitButtonText,
            },
          }
          const window = iFrame.contentWindow!
          // contentWindow will always be present as this is only called in response to the iFrameLoaded event
          window.postMessage(message, captureAppOrigin)
          setIsLoading(false)
          break
        }
        case 'error':
          props.onError(eventData.data)
          break
        case 'success':
          props.onSuccess(eventData.data)
          break
        case 'onClickBack':
          props.onClickBack()
          break
        default:
          break
      }
    }
  }

  useEffect(() => {
    window.addEventListener('message', listenerHandler, false)
    return () => {
      window.removeEventListener('message', listenerHandler, false)
    }
  }, [])

  const iframeSRC = captureAppOrigin + '?origin=' + window.location.origin

  return (
    <ResponsiveContainer sx={props.sx}>
      {isLoading && (
        <div
          style={{
            textAlign: 'center',
            marginTop: '24px',
          }}
        >
          <CircularProgress
            sx={{ color: props.theme?.palette?.primary?.main }}
          />
        </div>
      )}
      <iframe
        src={iframeSRC}
        ref={el => {
          if (el) iFrame = el
          return el
        }}
        id='payment-capture-iframe'
        style={{
          display: isIframeLoaded ? 'block' : 'none',
          minHeight: '600px',
          width: '100%',
          height: '100%',
          border: 'none',
          ...(props.sx?.iframe ? props.sx.iframe : {}),
        }}
      ></iframe>
    </ResponsiveContainer>
  )
}

Card.defaultProps = {
  onSuccess: (data: CardAdditionSuccess) =>
    console.log('onSuccess handler triggered by iFrame card addition', data),
  onError: (data: CardAdditionError) =>
    console.log('onError handler triggered by iFrame card addition :', data),
  onClickBack: () => console.log('onClickBack handler triggered'),
  clientSecret: '',
  payerId: '',
  parentOrigin: '',
  publicKey: '',
  environment: '',
  theme: {},
  isStart: false,
  disableSubmitOnSuccess: false,
  submitButtonText: '',
}

export default Card
